// EditRecordTaxSection.jsx — 계좌·세금정보 연결 모달의 세금정보 섹션 (EditRecordModal에서 분리)
// taxTypeEnabled(회사별 스위치) ON 일 때만 과세유형(개인/사업자) 선택 UI 노출 — OFF면 기존과 동일
import React, { useEffect, useState } from 'react';
import { todayKST } from '../../../utils/date';
import SingleDatePicker from '../../../components/SingleDatePicker';
import { apiClient } from '../../../api/apiClient';
import {
  taxLabel, getItemStatus, canAutoSelectNewInfo, changeModeForDate,
  sectionStyle, titleStyle, hintStyle, inputStyle, gridStyle, smallBtnStyle,
  primaryBtnStyle, dangerBtnStyle, statusBadgeStyle, optionMetaStyle,
  optionDescriptionStyle, errorStyle, effectiveDateBadgeStyle, datePanelStyle,
} from './editRecordModal.shared';

export function EditRecordTaxSection({
  record,
  activeTaxList,
  effectiveTaxId,
  settlementDateYmd,
  selTax,
  setSelTax,
  onAddTaxInfo,
  onDeleteTaxInfo,
  taxTypeEnabled = false,
}) {
  const today = todayKST();
  const [addingTax, setAddingTax] = useState(false);
  const [taxName, setTaxName] = useState('');
  const [taxRn, setTaxRn] = useState('');
  const [taxRate, setTaxRate] = useState('3.3');
  const [taxEffectiveFrom, setTaxEffectiveFrom] = useState(today);
  const [taxReason, setTaxReason] = useState('');
  const [taxErr, setTaxErr] = useState('');
  const [taxSaving, setTaxSaving] = useState(false);
  // 과세유형 (스위치 ON 전용) — 카탈로그는 등록 폼 최초 오픈 시 지연 로드
  const [taxTypes, setTaxTypes] = useState(null);
  const [taxTypeCode, setTaxTypeCode] = useState('individual');
  const [bizNumber, setBizNumber] = useState('');
  const [bizName, setBizName] = useState('');

  useEffect(() => {
    if (!taxTypeEnabled || !addingTax || taxTypes !== null) return;
    (async () => {
      const r = await apiClient.get('/api/admin/payout/tax-types');
      if (r.ok && r.data?.success) setTaxTypes(r.data.data || []);
      else setTaxTypes([]);
    })();
  }, [taxTypeEnabled, addingTax, taxTypes]);

  const selectedType = (taxTypes || []).find((t) => t.code === taxTypeCode) || null;
  const isBusinessType = taxTypeEnabled && selectedType?.calc_method === 'vat_add';

  const handleAddTax = async () => {
    setTaxErr('');
    // 사업자: 상호+사업자번호만 필수 (이름=상호로 저장, 주민번호 생략)
    if (isBusinessType) {
      if (!bizName.trim()) { setTaxErr('상호를 입력하세요.'); return; }
      if (!bizNumber.trim()) { setTaxErr('사업자등록번호를 입력하세요.'); return; }
    } else {
      if (!taxName.trim()) { setTaxErr('이름을 입력하세요.'); return; }
      if (!taxRn.trim()) { setTaxErr('주민등록번호를 입력하세요.'); return; }
    }
    if (!taxEffectiveFrom) { setTaxErr('적용일을 선택하세요.'); return; }
    setTaxSaving(true);
    try {
      const newId = await onAddTaxInfo(
        record.user_id,
        isBusinessType ? bizName.trim() : taxName.trim(),
        isBusinessType ? '' : taxRn.trim(),
        Number(taxRate) || 3.3,
        {
          effectiveFrom: taxEffectiveFrom,
          changeMode: changeModeForDate(taxEffectiveFrom),
          reason: taxReason.trim(),
          // 스위치 ON 이고 기본(개인)이 아닌 유형을 골랐을 때만 전송 — 개인은 legacy 동작 유지
          ...(taxTypeEnabled && taxTypeCode !== 'individual' ? {
            taxTypeCode,
            businessRegistrationNumber: bizNumber.trim(),
            businessName: bizName.trim(),
          } : {}),
        },
      );
      if (newId) {
        if (canAutoSelectNewInfo(taxEffectiveFrom, activeTaxList, settlementDateYmd)) setSelTax(newId);
        setAddingTax(false);
        setTaxName('');
        setTaxRn('');
        setTaxRate('3.3');
        setTaxReason('');
        setTaxTypeCode('individual');
        setBizNumber('');
        setBizName('');
      }
    } finally {
      setTaxSaving(false);
    }
  };
  const handleDeleteTax = (id) => {
    if (!window.confirm('이 세금정보를 비활성화할까요? 이미 연결된 정산 기록의 스냅샷은 유지됩니다.')) return;
    if (String(selTax) === String(id)) setSelTax('');
    onDeleteTaxInfo(id, record.user_id);
  };

  return (
    <div style={sectionStyle}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <div style={{ minWidth: 0 }}>
          <div style={titleStyle}>세금정보</div>
          <div style={hintStyle}>정산일 기준 세율과 신고 정보를 선택하거나 새로 등록합니다.</div>
        </div>
        <button style={smallBtnStyle} onClick={() => { setAddingTax((v) => !v); setTaxErr(''); }}>
          {addingTax ? '닫기' : '+ 세금정보 등록'}
        </button>
      </div>

      <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 12, color: 'var(--text-muted)' }}>
        <input type="radio" name="selTax" checked={!selTax} onChange={() => setSelTax('')} />
        선택 안 함
      </label>
      {activeTaxList.map((t) => {
        const status = getItemStatus(t, effectiveTaxId, settlementDateYmd);
        const disabled = !status.selectable;
        return (
        <div key={t.id} style={{ display: 'flex', gap: 8, alignItems: 'center', minWidth: 0 }}>
          <label style={{ display: 'flex', gap: 8, alignItems: 'center', flex: 1, fontSize: 12, color: 'var(--text)', cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.55 : 1 }}>
            <input type="radio" name="selTax" checked={String(selTax) === String(t.id)} disabled={disabled} onChange={() => setSelTax(t.id)} />
            <span style={optionMetaStyle}>
              <span style={{ minWidth: 0, overflowWrap: 'anywhere' }}>{taxLabel(t)}</span>
              <span style={statusBadgeStyle(status.tone)}>{status.label}</span>
              {status.description && <span style={optionDescriptionStyle}>{status.description}</span>}
            </span>
          </label>
          <button style={dangerBtnStyle} onClick={() => handleDeleteTax(t.id)}>삭제</button>
        </div>
        );
      })}
      {activeTaxList.length === 0 && <div style={hintStyle}>등록된 세금정보가 없습니다.</div>}

      {addingTax && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, paddingTop: 4 }}>
          {taxTypeEnabled && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <select
                style={inputStyle}
                value={taxTypeCode}
                onChange={(e) => setTaxTypeCode(e.target.value)}
              >
                {taxTypes === null && <option value="individual">불러오는 중...</option>}
                {(taxTypes || []).map((t) => (
                  <option key={t.code} value={t.code}>{t.label}</option>
                ))}
              </select>
              <div style={hintStyle}>
                {isBusinessType
                  ? '사업자: 공제 없이 정산액에 부가세를 더해 지급합니다.'
                  : '개인: 정산액에서 세율만큼 원천징수 공제 후 지급합니다.'}
              </div>
            </div>
          )}
          <div style={gridStyle}>
            {isBusinessType
              ? <input style={inputStyle} placeholder="상호" value={bizName} onChange={(e) => setBizName(e.target.value)} />
              : <input style={inputStyle} placeholder="이름" value={taxName} onChange={(e) => setTaxName(e.target.value)} />}
            <div style={effectiveDateBadgeStyle}>적용 시작일 {taxEffectiveFrom}</div>
          </div>
          <div style={datePanelStyle}>
            <div style={hintStyle}>달력에서 날짜를 누르면 그 날짜부터 이 세금정보가 적용됩니다.</div>
            <SingleDatePicker value={taxEffectiveFrom} onChange={setTaxEffectiveFrom} />
          </div>
          {isBusinessType ? (
            <div style={gridStyle}>
              <input style={inputStyle} placeholder="사업자등록번호" value={bizNumber} onChange={(e) => setBizNumber(e.target.value)} />
            </div>
          ) : (
            <div style={gridStyle}>
              <input style={inputStyle} placeholder="주민등록번호" value={taxRn} onChange={(e) => setTaxRn(e.target.value)} />
              <input style={inputStyle} placeholder="세율 (%)" value={taxRate} onChange={(e) => setTaxRate(e.target.value)} type="text" inputMode="decimal" />
            </div>
          )}
          <input style={inputStyle} placeholder="변경 사유 또는 메모" value={taxReason} onChange={(e) => setTaxReason(e.target.value)} />
          {taxErr && <span style={errorStyle}>{taxErr}</span>}
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button style={primaryBtnStyle} onClick={handleAddTax} disabled={taxSaving}>
              {taxSaving ? '등록 중...' : '등록'}
            </button>
            <button style={smallBtnStyle} onClick={() => setAddingTax(false)}>취소</button>
          </div>
        </div>
      )}
    </div>
  );
}
